import { applyCapabilityImplications } from '../../lib/capabilities/implications';
import { GEOFENCE_CAPABILITY_KEYS as RESTRICTION_CAPABILITY_KEYS } from '../../lib/capabilities/geofence';
import { ADMIN_LOCKED_CAPABILITY_KEYS } from '../../lib/capabilities/adminRole';

export type CapabilityMap = Record<string, boolean>;
export type PlatformAccess = 'office_and_field' | 'field_only';

export type CapabilityPerm = { key: string; label: string; groupLabel: string };

export const WEB_CAPABILITY_PERMS: CapabilityPerm[] = [
  { key: 'web.projekte.view', label: 'Ansehen', groupLabel: 'Projekte' },
  { key: 'web.projekte.edit', label: 'Bearbeiten', groupLabel: 'Projekte' },
  { key: 'web.rechnungen.view', label: 'Ansehen', groupLabel: 'Rechnungen' },
  { key: 'web.rechnungen.edit', label: 'Bearbeiten', groupLabel: 'Rechnungen' },
  { key: 'web.rechnungen.approve', label: 'Eingangsrechnungen freigeben', groupLabel: 'Rechnungen' },
  { key: 'web.rechnungen.datev', label: 'DATEV-Export', groupLabel: 'Rechnungen' },
  { key: 'web.kontakte.view', label: 'Ansehen', groupLabel: 'Kontakte' },
  { key: 'web.kontakte.edit', label: 'Bearbeiten', groupLabel: 'Kontakte' },
  { key: 'web.plantafel.view', label: 'Ansehen', groupLabel: 'Plantafel' },
  { key: 'web.plantafel.edit', label: 'Bearbeiten', groupLabel: 'Plantafel' },
  { key: 'web.aufgaben.own', label: 'Eigene', groupLabel: 'To-dos' },
  { key: 'web.aufgaben.view', label: 'Alle ansehen', groupLabel: 'To-dos' },
  { key: 'web.aufgaben.edit', label: 'Alle bearbeiten', groupLabel: 'To-dos' },
  { key: 'web.posteingang.view', label: 'Ansehen', groupLabel: 'Posteingang' },
  { key: 'web.posteingang.edit', label: 'Bearbeiten', groupLabel: 'Posteingang' },
  { key: 'web.kichat.view', label: 'Zugriff', groupLabel: 'KI Chat' },
  { key: 'web.stammdaten.view', label: 'Ansehen', groupLabel: 'Stammdaten' },
  { key: 'web.stammdaten.edit', label: 'Bearbeiten', groupLabel: 'Stammdaten' },
  { key: 'web.ausschreibungen.view', label: 'Ansehen', groupLabel: 'Ausschreibungen' },
  { key: 'web.ausschreibungen.edit', label: 'Bearbeiten', groupLabel: 'Ausschreibungen' },
  { key: 'web.vorlagen.view', label: 'Ansehen', groupLabel: 'Vorlagen' },
  { key: 'web.vorlagen.edit', label: 'Bearbeiten', groupLabel: 'Vorlagen' },
  { key: 'web.fuhrpark.view', label: 'Ansehen', groupLabel: 'Fuhrpark' },
  { key: 'web.fuhrpark.edit', label: 'Bearbeiten', groupLabel: 'Fuhrpark' },
  { key: 'web.hr.view', label: 'Ansehen', groupLabel: 'Mitarbeiter' },
  { key: 'web.hr.edit', label: 'Bearbeiten', groupLabel: 'Mitarbeiter' },
  { key: 'web.hr.approve', label: 'Lohn freigeben', groupLabel: 'Mitarbeiter' },
  { key: 'web.hr.internal', label: 'Interne (Büro)', groupLabel: 'Mitarbeiter' },
  { key: 'web.einstellungen.view', label: 'Ansehen', groupLabel: 'Einstellungen' },
  { key: 'web.einstellungen.edit', label: 'Bearbeiten', groupLabel: 'Einstellungen' },
  { key: 'web.einstellungen.invite', label: 'Nutzer einladen', groupLabel: 'Einstellungen' },
];

export const APP_CAPABILITY_PERMS: CapabilityPerm[] = [
  { key: 'app.zeit.view', label: 'Einträge sehen', groupLabel: 'Zeiterfassung' },
  { key: 'app.zeit.edit', label: 'Einträge bearbeiten', groupLabel: 'Zeiterfassung' },
  { key: 'app.zeit.geofence_in', label: 'Einstempeln nur vor Ort', groupLabel: 'Zeiterfassung' },
  { key: 'app.zeit.geofence_out', label: 'Ausstempeln nur vor Ort', groupLabel: 'Zeiterfassung' },
  { key: 'app.zeit.team_edit', label: 'Zeiten fürs Team eintragen', groupLabel: 'Zeiterfassung' },
  { key: 'app.kalender.view', label: 'Kalender ansehen', groupLabel: 'Kalender' },
  { key: 'app.kalender.update', label: 'Termin bearbeiten', groupLabel: 'Kalender' },
  { key: 'app.kalender.edit', label: 'Termin hinzufügen', groupLabel: 'Kalender' },
  { key: 'app.kalender.team_view', label: 'Team-Termine sehen', groupLabel: 'Kalender' },
  { key: 'app.kontakte.view', label: 'Kontakte einsehen', groupLabel: 'Kontakte' },
  { key: 'app.preise.view', label: 'Preise anzeigen', groupLabel: 'Preise' },
  { key: 'app.rechnungen.create', label: 'Rechnung erstellen', groupLabel: 'Rechnungen' },
  { key: 'app.kichat.angebote', label: 'KI-Angebote erstellen', groupLabel: 'KI Chat' },
  { key: 'app.kommentare.view', label: 'Kommentare sehen', groupLabel: 'Kommentare' },
  { key: 'app.kommentare.edit', label: 'Kommentare schreiben', groupLabel: 'Kommentare' },
];

/** Mirrors server resolve (`resolveEffectiveCapabilities`) without the module filter. */
export function resolveMemberCapabilities(input: {
  role: string;
  platformAccess: PlatformAccess;
  presetCapabilities?: CapabilityMap;
  overrideCapabilities?: CapabilityMap;
}): CapabilityMap {
  const preset = input.presetCapabilities ?? {};
  const overrides = input.overrideCapabilities ?? {};
  // Owners (and admins without a role) start with everything; owners only take app overrides.
  const fullBase = input.role === 'owner' || (input.role === 'admin' && !Object.keys(preset).length);
  const caps: CapabilityMap = {};
  for (const perm of [...WEB_CAPABILITY_PERMS, ...APP_CAPABILITY_PERMS]) {
    // Geofence caps restrict instead of grant — "Vollzugriff" must not enable them.
    caps[perm.key] = fullBase
      ? !RESTRICTION_CAPABILITY_KEYS.includes(perm.key as (typeof RESTRICTION_CAPABILITY_KEYS)[number])
      : Boolean(preset[perm.key]);
    if (input.role === 'owner' && !perm.key.startsWith('app.')) continue;
    const override = overrides[perm.key];
    if (typeof override === 'boolean') caps[perm.key] = override;
  }
  if (input.platformAccess === 'field_only') {
    for (const perm of WEB_CAPABILITY_PERMS) caps[perm.key] = false;
  } else if (input.role === 'admin') {
    for (const key of ADMIN_LOCKED_CAPABILITY_KEYS) caps[key] = true;
  }
  return applyCapabilityImplications(caps);
}

export type CapabilityDeviation = {
  key: string;
  label: string;
  kind: 'allow' | 'deny';
};

export function listCapabilityDeviations(preset: CapabilityMap, overrides: CapabilityMap): CapabilityDeviation[] {
  const out: CapabilityDeviation[] = [];
  for (const perm of [...WEB_CAPABILITY_PERMS, ...APP_CAPABILITY_PERMS]) {
    const value = overrides[perm.key];
    if (typeof value !== 'boolean') continue;
    const presetValue = Boolean(preset[perm.key]);
    if (value === presetValue) continue;
    out.push({
      key: perm.key,
      label: `${perm.groupLabel} · ${perm.label}`,
      kind: value ? 'allow' : 'deny',
    });
  }
  return out;
}

export const ACCESS_LABEL: Record<PlatformAccess, string> = {
  office_and_field: 'Web & App',
  field_only: 'Nur App',
};

export const ACCESS_CHIP: Record<PlatformAccess, string> = {
  office_and_field: 'bg-emerald-100 text-emerald-700',
  field_only: 'bg-amber-100 text-amber-700',
};

export type InvitePermissionItem = { kind: 'allow' | 'deny'; label: string };
export type InvitePermissionBucket = { items: InvitePermissionItem[] };
export type InvitePermissionLists = {
  web: InvitePermissionBucket;
  app: InvitePermissionBucket;
};

type SummaryGroupDef = {
  key: string;
  scope: 'web' | 'app';
  label: string;
  permKeys: string[];
  /** Nur für die Einladungs-Zusammenfassung: fehlende Rechte als Minus-Zeile anzeigen */
  notableWhenDenied?: boolean;
};

const SUMMARY_GROUPS: SummaryGroupDef[] = [
  { key: 'projekte', scope: 'web', label: 'Projekte', permKeys: ['web.projekte.view', 'web.projekte.edit'] },
  { key: 'rechnungen', scope: 'web', label: 'Rechnungen', permKeys: ['web.rechnungen.view', 'web.rechnungen.edit', 'web.rechnungen.approve', 'web.rechnungen.datev'], notableWhenDenied: true },
  { key: 'kontakte', scope: 'web', label: 'Kontakte', permKeys: ['web.kontakte.view', 'web.kontakte.edit'] },
  { key: 'plantafel', scope: 'web', label: 'Plantafel', permKeys: ['web.plantafel.view', 'web.plantafel.edit'] },
  { key: 'aufgaben', scope: 'web', label: 'To-dos', permKeys: ['web.aufgaben.own', 'web.aufgaben.view', 'web.aufgaben.edit'] },
  { key: 'posteingang', scope: 'web', label: 'Posteingang', permKeys: ['web.posteingang.view', 'web.posteingang.edit'] },
  { key: 'kichat', scope: 'web', label: 'KI Chat', permKeys: ['web.kichat.view'] },
  { key: 'stammdaten', scope: 'web', label: 'Stammdaten', permKeys: ['web.stammdaten.view', 'web.stammdaten.edit'], notableWhenDenied: true },
  { key: 'ausschreibungen', scope: 'web', label: 'Ausschreibungen', permKeys: ['web.ausschreibungen.view', 'web.ausschreibungen.edit'] },
  { key: 'vorlagen', scope: 'web', label: 'Vorlagen', permKeys: ['web.vorlagen.view', 'web.vorlagen.edit'] },
  { key: 'fuhrpark', scope: 'web', label: 'Fuhrpark', permKeys: ['web.fuhrpark.view', 'web.fuhrpark.edit'] },
  { key: 'hr', scope: 'web', label: 'Mitarbeiter', permKeys: ['web.hr.view', 'web.hr.edit', 'web.hr.approve', 'web.hr.internal'], notableWhenDenied: true },
  { key: 'einstellungen', scope: 'web', label: 'Einstellungen', permKeys: ['web.einstellungen.view', 'web.einstellungen.edit', 'web.einstellungen.invite'], notableWhenDenied: true },
  { key: 'zeit', scope: 'app', label: 'Zeiterfassung', permKeys: ['app.zeit.view', 'app.zeit.edit', 'app.zeit.team_edit'] },
  { key: 'kalender', scope: 'app', label: 'Kalender', permKeys: ['app.kalender.view', 'app.kalender.update', 'app.kalender.edit', 'app.kalender.team_view'], notableWhenDenied: true },
  { key: 'app_kontakte', scope: 'app', label: 'Kontakte', permKeys: ['app.kontakte.view'] },
  { key: 'preise', scope: 'app', label: 'Preise', permKeys: ['app.preise.view'], notableWhenDenied: true },
  { key: 'app_rechnungen', scope: 'app', label: 'Rechnungen (App)', permKeys: ['app.rechnungen.create'], notableWhenDenied: true },
  { key: 'app_kichat', scope: 'app', label: 'KI Chat (App)', permKeys: ['app.kichat.angebote'], notableWhenDenied: true },
  { key: 'app_kommentare', scope: 'app', label: 'Kommentare (App)', permKeys: ['app.kommentare.view', 'app.kommentare.edit'] },
];

function inviteAllowedLabel(group: SummaryGroupDef, caps: CapabilityMap): string | null {
  const has = (key: string) => Boolean(caps[key]);
  switch (group.key) {
    case 'projekte':
      if (has('web.projekte.edit')) return 'Projekte und Angebote anlegen & bearbeiten';
      if (has('web.projekte.view')) return 'Projekte und Angebote ansehen';
      return null;
    case 'rechnungen':
      if (has('web.rechnungen.edit') && has('web.rechnungen.approve')) return 'Rechnungen bearbeiten und freigeben';
      if (has('web.rechnungen.edit')) return 'Rechnungen bearbeiten';
      if (has('web.rechnungen.view')) return 'Rechnungen ansehen';
      return null;
    case 'kontakte':
      if (has('web.kontakte.edit')) return 'Kontakte pflegen';
      if (has('web.kontakte.view')) return 'Kontakte ansehen';
      return null;
    case 'plantafel':
      if (has('web.plantafel.edit')) return 'Termine in der Plantafel planen';
      if (has('web.plantafel.view')) return 'Plantafel einsehen';
      return null;
    case 'aufgaben':
      if (has('web.aufgaben.edit')) return 'Alle To-dos im Team bearbeiten';
      if (has('web.aufgaben.view')) return 'Alle To-dos im Team ansehen, eigene bearbeiten';
      if (has('web.aufgaben.own')) return 'Eigene To-dos verwalten';
      return null;
    case 'posteingang':
      if (has('web.posteingang.edit')) return 'Posteingang ansehen und bearbeiten';
      if (has('web.posteingang.view')) return 'Posteingang ansehen';
      return null;
    case 'kichat':
      return has('web.kichat.view') ? 'KI Chat nutzen' : null;
    case 'stammdaten':
      if (has('web.stammdaten.edit')) return 'Stammdaten pflegen';
      if (has('web.stammdaten.view')) return 'Stammdaten ansehen';
      return null;
    case 'ausschreibungen':
      if (has('web.ausschreibungen.edit')) return 'Ausschreibungen bearbeiten';
      if (has('web.ausschreibungen.view')) return 'Ausschreibungen ansehen';
      return null;
    case 'vorlagen':
      if (has('web.vorlagen.edit')) return 'Vorlagen bearbeiten';
      if (has('web.vorlagen.view')) return 'Vorlagen ansehen';
      return null;
    case 'fuhrpark':
      if (has('web.fuhrpark.edit')) return 'Fuhrpark bearbeiten';
      if (has('web.fuhrpark.view')) return 'Fuhrpark ansehen';
      return null;
    case 'hr':
      if (has('web.hr.approve')) return 'Lohn freigeben';
      if (has('web.hr.edit') && has('web.hr.internal')) return 'Mitarbeiter bearbeiten';
      if (has('web.hr.edit')) return 'Außendienst-Mitarbeiter bearbeiten';
      if (has('web.hr.view') && has('web.hr.internal')) return 'Mitarbeiter ansehen';
      if (has('web.hr.view')) return 'Außendienst-Mitarbeiter ansehen';
      return null;
    case 'einstellungen':
      if (has('web.einstellungen.edit')) return 'Einstellungen bearbeiten';
      if (has('web.einstellungen.invite')) return 'Nutzer einladen';
      if (has('web.einstellungen.view')) return 'Einstellungen ansehen';
      return null;
    case 'zeit': {
      const team = has('app.zeit.team_edit') ? ', Zeiten fürs Team eintragen' : '';
      if (has('app.zeit.edit')) return `In der App ein-/ausstempeln (Zeiteinträge bearbeiten)${team}`;
      if (has('app.zeit.view')) return `Zeiteinträge in der App ansehen, Urlaub und Krankheit beantragen (ohne Ein-/Ausstempeln)${team}`;
      return null;
    }
    case 'kalender': {
      const team = has('app.kalender.team_view') ? ', Team-Termine sehen' : '';
      if (has('app.kalender.edit') && has('app.kalender.update')) return `Termine hinzufügen und bearbeiten${team}`;
      if (has('app.kalender.edit')) return `Termine zum Kalender hinzufügen${team}`;
      if (has('app.kalender.update')) return `Termine in der App bearbeiten${team}`;
      if (has('app.kalender.view')) return `Kalender in der App ansehen${team}`;
      return null;
    }
    case 'app_kontakte':
      return has('app.kontakte.view') ? 'Kontakte in der App einsehen' : null;
    case 'preise':
      return has('app.preise.view') ? 'Preise in der App sehen' : null;
    case 'app_rechnungen':
      return has('app.rechnungen.create') ? 'Rechnungen in der App erstellen' : null;
    case 'app_kichat':
      return has('app.kichat.angebote') ? 'Angebote per KI-Chat in der App erstellen' : null;
    case 'app_kommentare':
      if (has('app.kommentare.edit')) return 'Projekt-Kommentare in der App lesen und schreiben';
      if (has('app.kommentare.view')) return 'Projekt-Kommentare in der App lesen';
      return null;
    default:
      return null;
  }
}

function inviteDeniedLabel(group: SummaryGroupDef): string {
  switch (group.key) {
    case 'rechnungen':
      return 'Keine Rechnungen';
    case 'hr':
      return 'Kein Lohn';
    case 'einstellungen':
      return 'Keine Einstellungen';
    case 'projekte':
      return 'Keine Projekte';
    case 'kontakte':
      return group.scope === 'app' ? 'Keine Kontakte in der App' : 'Keine Kontakte';
    case 'plantafel':
      return 'Keine Plantafel';
    case 'aufgaben':
      return 'Keine To-dos';
    case 'posteingang':
      return 'Kein Posteingang';
    case 'kichat':
      return 'Kein KI Chat';
    case 'stammdaten':
      return 'Keine Stammdaten';
    case 'ausschreibungen':
      return 'Keine Ausschreibungen';
    case 'vorlagen':
      return 'Keine Vorlagen';
    case 'fuhrpark':
      return 'Kein Fuhrpark';
    case 'zeit':
      return 'Keine Zeiterfassung in der App';
    case 'kalender':
      return 'Kein Kalender in der App';
    case 'preise':
      return 'Keine Preise in der App';
    case 'app_rechnungen':
      return 'Keine Rechnungen in der App';
    default:
      return `Keine ${group.label}`;
  }
}

/**
 * `availablePermKeys`: nur Bereiche zeigen, von denen mindestens ein Recht angeboten wird
 * (abgewählte Module fehlen, siehe availableRoleEditorPermKeys).
 */
export function buildInvitePermissionLists(
  caps: CapabilityMap,
  platformAccess: PlatformAccess,
  availablePermKeys?: ReadonlySet<string>,
): InvitePermissionLists {
  const webItems: InvitePermissionItem[] = [];
  const appItems: InvitePermissionItem[] = [];

  for (const group of SUMMARY_GROUPS) {
    if (group.scope === 'web' && platformAccess === 'field_only') continue;
    if (availablePermKeys && !group.permKeys.some((key) => availablePermKeys.has(key))) continue;

    const allowedLabel = inviteAllowedLabel(group, caps);
    const target = group.scope === 'web' ? webItems : appItems;

    if (allowedLabel) {
      target.push({ kind: 'allow', label: allowedLabel });
      continue;
    }

    if (!group.notableWhenDenied) continue;
    target.push({ kind: 'deny', label: inviteDeniedLabel(group) });
  }

  return {
    web: { items: webItems },
    app: { items: appItems },
  };
}

export function pruneOverridesToPreset(preset: CapabilityMap, overrides: CapabilityMap): CapabilityMap {
  const out: CapabilityMap = {};
  for (const [key, value] of Object.entries(overrides)) {
    if (typeof value !== 'boolean') continue;
    if (Boolean(preset[key]) === value) continue;
    out[key] = value;
  }
  return out;
}
